import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { identify, initAnalytics, resetAnalytics, trackPageview } from '../lib/analytics';

/**
 * Headless analytics bridge. Mounted once inside the router + auth providers, it:
 *   1. boots PostHog (no-op without VITE_POSTHOG_KEY),
 *   2. identifies the authed user by id on session load (and resets on sign-out),
 *   3. emits an SPA `$pageview` on every route change.
 *
 * Renders nothing. All calls are key-gated inside `../lib/analytics`.
 */
export function Analytics(): null {
  const location = useLocation();
  const { user, status } = useAuth();
  const identifiedFor = useRef<string | null>(null);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Emit a pageview per navigation (path + query, no hash).
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Link events to the user id once the session resolves; reset on sign-out.
  useEffect(() => {
    if (status === 'authenticated' && user?.id) {
      if (identifiedFor.current !== user.id) {
        identify(user.id);
        identifiedFor.current = user.id;
      }
    } else if (status === 'anonymous' && identifiedFor.current !== null) {
      resetAnalytics();
      identifiedFor.current = null;
    }
  }, [status, user?.id]);

  return null;
}
